import { AvailabilityType, type IBookableresourceAvailability, type IResource } from "../../interfaces/resource";

type AvailabilityWindow = { start: string | null; end: string | null };

const AVAILABLE_TYPE_VALUE = 390950004;

const toIsoString = (value: Date | string | null | undefined): string | null => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  return String(value);
};

const mapAvailabilityWindows = (
  availability: IBookableresourceAvailability[],
  availabilityType: number
): AvailabilityWindow[] => {
  return availability
    .filter((a) => a.illumina_availabilitytype === availabilityType)
    .map((a) => ({
      start: toIsoString(a.illumina_startdate),
      end: toIsoString(a.illumina_enddate)
    }));
};

// Normalizes a resource object to the shape expected by mapResources
export function normalizeResource(raw: IResource): any {
  const availability = raw.availability ?? [];

  return {
    id: raw.id,
    title: raw.title,
    startLocationType: raw.startLocationType ?? null,
    endLocationType: raw.endLocationType ?? null,
    startLocationLabel: raw.startLocationLabel ?? null,
    endLocationLabel: raw.endLocationLabel ?? null,
    resourceAddressCoordinates: raw.resourceAddressCoordinates ?? null,
    resourceAddressLabel: raw.resourceAddressLabel ?? null,
    availability: {
      contracted: mapAvailabilityWindows(availability, AvailabilityType.ContractedHours),
      available: mapAvailabilityWindows(availability, AVAILABLE_TYPE_VALUE),
      preferred: mapAvailabilityWindows(availability, AvailabilityType.preferred),
      unavailable: mapAvailabilityWindows(availability, AvailabilityType.Unavailable)
    },
    contractedHours: mapAvailabilityWindows(availability, AvailabilityType.ContractedHours),
    baseRateAUD: raw.baseRateAUD ?? null,
    minimumWorkHoursPerPayroll: raw.minimumWorkHoursPerPayroll ?? null,
    defaultWorkHoursPerPayroll: raw.defaultWorkHoursPerPayroll ?? null,
    maximumWorkHourPerPayroll: raw.maximumWorkHourPerPayroll ?? null,
    maximumWorkingHoursPerDay: raw.maximumWorkingHoursPerDay ?? null,
    currentEmploymentType: raw.currentEmploymentType ?? null,
    applicableAward: raw.applicableAward ?? null,
    minimumBreakBetweenShifts: raw.minimumBreakBetweenShifts ?? null,
    countPassiveSleepOverAsBreak: raw.countPassiveSleepOverAsBreak ?? null,
    payrollHourVariations: raw.payrollHourVariations ?? [],
    maxHoursPerShift: raw.maxHoursPerShift ?? null,
    homeCoordinates: raw.resourceAddressCoordinates ?? null,
    approvedLeaves: (raw.approvedLeaves ?? []).map((leave) => ({
      id: leave.id,
      start: toIsoString(leave.start),
      end: toIsoString(leave.end)
    })),
    characteristics: (raw.characteristics ?? []).map((c) => ({
      name: c.name,
      ratingValueId: c.ratingValueId,
      ratingName: c.ratingName,
      ratingModelName: c.ratingModelName,
      expiryDate: toIsoString(c.expiryDate)
    })),
    categories: (raw.categories ?? []).map((cat) => ({ name: cat.name })),
    siteLocation: (raw.siteLocation ?? []).map((site) => ({ name: site.illumina_name })),
    personalNote: raw.personalNote ?? null
  };
}
